import { useEffect, useRef, useState } from "react"
import { useAuth } from "@/hooks/use-auth"
import { cn } from "@/lib/utils"
import { Zap, Shield, CalendarDays } from "lucide-react"
import Link from "next/link"
import { AnimatePresence, motion } from "framer-motion"
import { getPlanTier } from "@/lib/membership"

interface WalletBalanceProps {
    className?: string
    forceShowParticles?: boolean
}

export function WalletBalance({ className, forceShowParticles }: WalletBalanceProps) {
    const { user, isLoading } = useAuth()
    const [diff, setDiff] = useState<number>(0)
    const [showDiff, setShowDiff] = useState(false)
    const prevBalanceRef = useRef<number | null>(null)

    useEffect(() => {
        if (user?.particle_balance === undefined) return

        if (prevBalanceRef.current !== null) {
            const difference = user.particle_balance - prevBalanceRef.current
            if (difference !== 0) {
                setDiff(difference)
                setShowDiff(true)
                const timer = setTimeout(() => setShowDiff(false), 3000)
                return () => clearTimeout(timer)
            }
        }
        prevBalanceRef.current = user.particle_balance
    }, [user?.particle_balance])

    if (isLoading) {
        return (
            <div className={cn("flex items-center gap-2 px-3 py-1.5 rounded-full bg-muted/50 animate-pulse h-[34px] w-[80px]", className)}>
                <div className="h-4 w-4 rounded-full bg-muted-foreground/20" />
                <div className="h-4 w-10 rounded bg-muted-foreground/20" />
            </div>
        )
    }

    if (!user) return null

    // Force free plan behavior if forceShowParticles is true, so particles are always displayed.
    const isFreePlan = forceShowParticles || !user.active_plan || user.active_plan.trim() === 'Free'

    // Calculate days left for paid plans
    let daysLeft = 0
    if (!isFreePlan && user.membership_expiry) {
        const expiryDate = new Date(user.membership_expiry)
        const today = new Date()
        const diffTime = expiryDate.getTime() - today.getTime()
        daysLeft = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    }

    const isPremium = getPlanTier(user.active_plan) === "premium"

    const iconColors = isFreePlan
        ? "bg-indigo-200 dark:bg-indigo-800/50 text-indigo-700 dark:text-indigo-400"
        : isPremium
            ? "bg-purple-200 dark:bg-purple-800/50 text-purple-700 dark:text-purple-400"
            : "bg-amber-200 dark:bg-amber-800/50 text-amber-700 dark:text-amber-400"

    const textColors = isFreePlan
        ? "text-indigo-700 dark:text-indigo-300"
        : isPremium
            ? "text-purple-600 dark:text-purple-400"
            : "text-amber-600 dark:text-amber-400"

    const planLabel = user.active_plan?.trim() || "Free"
    const hoverLabel = isFreePlan
        ? (user.particle_balance?.toLocaleString() ?? "0")
        : (daysLeft > 0 ? `${daysLeft}D` : "Expired")

    // Provide a better label if it's forced
    const displayLabel = forceShowParticles ? hoverLabel : planLabel

    // Ghost content is the wider of the two labels to size the container
    const ghostLabel = displayLabel.length >= hoverLabel.length ? displayLabel : hoverLabel

    return (
        <div className="relative flex items-center">
            <AnimatePresence>
                {showDiff && isFreePlan && (
                    <motion.div
                        initial={{ opacity: 0, y: 10, x: "-50%" }}
                        animate={{ opacity: 1, y: 20 }}
                        exit={{ opacity: 0, y: 10 }}
                        className={cn(
                            "absolute left-1/2 top-4 font-bold text-[10px] md:text-xs px-1.5 py-0.5 rounded-md shadow-sm pointer-events-none z-50 whitespace-nowrap",
                            diff > 0
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                        )}
                    >
                        {diff > 0 ? "+" : ""}{diff}
                    </motion.div>
                )}
            </AnimatePresence>

            <Link href="/dashboard/wallet">
                <div
                    className={cn(
                        "inline-flex items-center h-7 md:h-[34px] px-2 md:px-3 rounded-full border transition-all cursor-pointer group relative z-10 overflow-hidden",
                        isFreePlan
                            ? "bg-indigo-100 dark:bg-indigo-900/30 border-indigo-200 dark:border-indigo-800 hover:bg-indigo-200/80 dark:hover:bg-indigo-800/50"
                            : isPremium
                                ? "bg-purple-500/10 border-purple-500/30 hover:bg-purple-500/20"
                                : "bg-amber-500/10 border-amber-500/30 hover:bg-amber-500/20",
                        className
                    )}
                    title={isFreePlan ? "Your Particle Balance" : `${daysLeft}D remaining`}
                >
                    <div className="flex items-center gap-1.5 absolute inset-0 px-2 md:px-3 justify-center transition-all duration-300 transform translate-y-0 opacity-100 group-hover:-translate-y-full group-hover:opacity-0">
                        <div className={cn("flex items-center justify-center h-4 w-4 md:h-5 md:w-5 rounded-full shrink-0", forceShowParticles ? "bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400" : iconColors)}>
                            {forceShowParticles ? (
                                <Zap className="h-2.5 w-2.5 md:h-3 md:w-3 fill-current" />
                            ) : (
                                <Shield className="h-2.5 w-2.5 md:h-3 md:w-3 fill-current" />
                            )}
                        </div>
                        <span className={cn("text-[10px] md:text-sm font-bold whitespace-nowrap", forceShowParticles ? "text-slate-700 dark:text-slate-200 font-mono" : textColors)}>
                            {displayLabel}
                        </span>
                    </div>

                    {/* Hover State — slides up from below */}
                    <div className="flex items-center gap-1.5 absolute inset-0 px-2 md:px-3 justify-center transition-all duration-300 transform translate-y-full opacity-0 group-hover:translate-y-0 group-hover:opacity-100">
                        {isFreePlan ? (
                            <>
                                <div className="flex items-center justify-center h-4 w-4 md:h-5 md:w-5 rounded-full bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400 shrink-0">
                                    <Zap className="h-2.5 w-2.5 md:h-3 md:w-3 fill-current" />
                                </div>
                                <span className="text-[10px] md:text-sm font-bold text-slate-700 dark:text-slate-200 font-mono whitespace-nowrap">
                                    {hoverLabel}
                                </span>
                            </>
                        ) : (
                            <>
                                <div className={cn("flex items-center justify-center h-4 w-4 md:h-5 md:w-5 rounded-full shrink-0", iconColors)}>
                                    <CalendarDays className="h-2.5 w-2.5 md:h-3 md:w-3" />
                                </div>
                                <span className={cn("text-[10px] md:text-sm font-bold font-mono whitespace-nowrap", textColors)}>
                                    {hoverLabel}
                                </span>
                            </>
                        )}
                    </div>

                    {/* Ghost row — invisible, in normal flow to size the container width */}
                    <div className="flex items-center gap-1.5 invisible pointer-events-none select-none" aria-hidden="true">
                        <div className="h-4 w-4 md:h-5 md:w-5 shrink-0" />
                        <span className="text-[10px] md:text-sm font-bold whitespace-nowrap">
                            {ghostLabel}
                        </span>
                    </div>
                </div>
            </Link>
        </div>
    )
}
